import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pause, Play, XCircle, Plus, Users, Bell, Clock, LayoutGrid, List } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { dealService, type Deal } from '@/services/deal.service';
import { useMerchantSocket } from '@/services/socket.service';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';

export function ActiveDealsPage() {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [rushingCounts, setRushingCounts] = useState<Record<string, number>>({});

  // Get store ID from auth/merchant context
  const user = useAuthStore((s) => s.user);
  const storeId = user?.id; // simplified - in production would come from store selection

  // Socket.io for real-time updates
  const { isConnected, on } = useMerchantSocket(storeId);

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ['active-deals'],
    queryFn: dealService.getActiveDeals,
  });

  // Socket event handlers
  useEffect(() => {
    const unsubRush = on('rush:started', (payload) => {
      setRushingCounts((prev) => ({ ...prev, [payload.dealId]: payload.totalRushing }));
    });

    const unsubClaimed = on('deal:claimed', () => {
      queryClient.invalidateQueries({ queryKey: ['active-deals'] });
    });

    const unsubFully = on('deal:fully-claimed', (payload) => {
      toast.success(`Deal fully claimed: ${payload.dealId}`);
      queryClient.invalidateQueries({ queryKey: ['active-deals'] });
    });

    const unsubMetrics = on('metrics:updated', () => {
      queryClient.invalidateQueries({ queryKey: ['active-deals'] });
    });

    return () => {
      unsubRush();
      unsubClaimed();
      unsubFully();
      unsubMetrics();
    };
  }, [on, queryClient]);

  const pauseMutation = useMutation({
    mutationFn: dealService.pauseDeal,
    onSuccess: () => {
      toast.success('Deal paused');
      queryClient.invalidateQueries({ queryKey: ['active-deals'] });
    },
    onError: () => toast.error('Failed to pause deal'),
  });

  const resumeMutation = useMutation({
    mutationFn: dealService.resumeDeal,
    onSuccess: () => {
      toast.success('Deal resumed');
      queryClient.invalidateQueries({ queryKey: ['active-deals'] });
    },
    onError: () => toast.error('Failed to resume deal'),
  });

  const cancelMutation = useMutation({
    mutationFn: dealService.cancelDeal,
    onSuccess: () => {
      toast.success('Deal cancelled');
      queryClient.invalidateQueries({ queryKey: ['active-deals'] });
    },
    onError: () => toast.error('Failed to cancel deal'),
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Active Deals</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isConnected ? (
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-green-500" /> Live updates connected
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-gray-400" /> Connecting...
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                'p-2',
                viewMode === 'grid' ? 'bg-primary text-white' : 'text-gray-500'
              )}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'p-2',
                viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-500'
              )}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          <Link to="/deals/create">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Deal
            </Button>
          </Link>
        </div>
      </div>

      {deals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="mb-4 text-lg text-muted-foreground">No active deals right now</p>
            <Link to="/deals/create">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create Your First Deal
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div
          className={cn(
            viewMode === 'grid'
              ? 'grid gap-4 md:grid-cols-2 lg:grid-cols-3'
              : 'space-y-4'
          )}
        >
          {deals.map((deal) => (
            <DealCard
              key={deal.id}
              deal={deal}
              viewMode={viewMode}
              rushingCount={rushingCounts[deal.id] || 0}
              onPause={() => pauseMutation.mutate(deal.id)}
              onResume={() => resumeMutation.mutate(deal.id)}
              onCancel={() => {
                if (confirm('Are you sure you want to cancel this deal?')) {
                  cancelMutation.mutate(deal.id);
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DealCard({
  deal,
  viewMode,
  rushingCount,
  onPause,
  onResume,
  onCancel,
}: {
  deal: Deal;
  viewMode: 'grid' | 'list';
  rushingCount: number;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
}) {
  const currentTier = deal.decayCurve?.tiers[0];
  const slotsRemaining = deal.slotsTotal - deal.slotsClaimed;
  const isPaused = deal.status === 'paused';

  // Calculate time remaining
  const expiresAt = deal.expiresAt ? new Date(deal.expiresAt) : null;
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    if (!expiresAt) return;

    const update = () => {
      const now = new Date();
      const diff = expiresAt.getTime() - now.getTime();
      if (diff <= 0) {
        setTimeRemaining('Expired');
        return;
      }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${mins}:${secs.toString().padStart(2, '0')}`);
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (viewMode === 'list') {
    return (
      <Card>
        <CardContent className="flex items-center gap-4 p-4">
          {deal.item.photoUrl && (
            <img
              src={deal.item.photoUrl}
              alt={deal.item.name}
              className="h-16 w-16 rounded-lg object-cover"
            />
          )}
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold">{deal.item.name}</h3>
            <p className="text-sm text-muted-foreground">
              {deal.item.originalPrice} {deal.item.currency}
            </p>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-center">
              <div className="font-semibold">{currentTier?.discountPercent || 0}%</div>
              <div className="text-xs text-muted-foreground">Discount</div>
            </div>
            <div className="text-center">
              <div className="font-semibold">
                {deal.slotsClaimed}/{deal.slotsTotal}
              </div>
              <div className="text-xs text-muted-foreground">Claimed</div>
            </div>
            <div className="text-center">
              <div className="font-semibold">{rushingCount}</div>
              <div className="text-xs text-muted-foreground">Rushing</div>
            </div>
            <div className="text-center">
              <div className="font-semibold">{timeRemaining || '--:--'}</div>
              <div className="text-xs text-muted-foreground">Remaining</div>
            </div>
          </div>
          <div className="flex gap-2">
            {isPaused ? (
              <Button size="sm" variant="outline" onClick={onResume}>
                <Play className="h-4 w-4" />
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={onPause}>
                <Pause className="h-4 w-4" />
              </Button>
            )}
            <Button size="sm" variant="destructive" onClick={onCancel}>
              <XCircle className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(isPaused && 'opacity-70')}>
      <CardContent className="p-0">
        {deal.item.photoUrl ? (
          <img
            src={deal.item.photoUrl}
            alt={deal.item.name}
            className="h-40 w-full object-cover"
          />
        ) : (
          <div className="flex h-40 items-center justify-center bg-gray-100">
            <span className="text-gray-400">No photo</span>
          </div>
        )}

        <div className="p-4">
          <div className="flex items-start justify-between">
            <h3 className="font-semibold">{deal.item.name}</h3>
            {isPaused && (
              <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
                Paused
              </span>
            )}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{timeRemaining || '--:--'}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 text-muted-foreground" />
              <span>
                {deal.slotsClaimed}/{deal.slotsTotal} claimed
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-orange-100 text-[10px] font-bold text-orange-600">
                {rushingCount}
              </span>
              <span>rushing now</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Bell className="h-3.5 w-3.5 text-muted-foreground" />
              <span>{deal.metrics?.notificationsSent || 0} notified</span>
            </div>
          </div>

          {/* Slots progress bar */}
          <div className="mt-3">
            <div className="h-2 overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${(deal.slotsClaimed / deal.slotsTotal) * 100}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {slotsRemaining} slot{slotsRemaining !== 1 ? 's' : ''} remaining
            </p>
          </div>

          {/* Actions */}
          <div className="mt-4 flex gap-2">
            {isPaused ? (
              <Button size="sm" variant="outline" onClick={onResume} className="flex-1">
                <Play className="mr-1 h-3.5 w-3.5" />
                Resume
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={onPause} className="flex-1">
                <Pause className="mr-1 h-3.5 w-3.5" />
                Pause
              </Button>
            )}
            <Button size="sm" variant="destructive" onClick={onCancel}>
              <XCircle className="mr-1 h-3.5 w-3.5" />
              End
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
